import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { InfoTag } from '@plato/ui-legacy/components/ui/info-tag';
import { TruncatedText } from '@plato/ui-legacy/components/ui/truncated-text';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { MapPinIcon, NavigationIcon, PlusIcon, Store, XIcon } from 'lucide-react';
import type React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import type { RouterOutput } from '../../../server/types/infer-trpc-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { CustomerInfoTags } from '../../shared/components/customer-info-tags/customer-info-tags.tsx';
import type { EventCreateDialogProps } from './event-create-dialog.tsx';
import { EventCreateDialog } from './event-create-dialog.tsx';
import { SuggestionFeedbackDialog } from './suggestion-feedback-dialog.tsx';

type DaySuggestionCardProps = {
    customer: RouterOutput['platoUser']['getManyCustomers']['getCustomersWithoutEvents'][number];
    rank: number;
    date?: string;
    onEventCreated?: () => void;
    hideScheduleButton?: boolean;
};

/**
 * Compact suggestion row for the event calendar: a single info tag on top, the customer name, and a
 * bottom line with ZIP + city plus the ZIP-tier proximity label ("En route" / "Route adjacent" /
 * "Similar region"). Tiers D/E (and no tier) show no proximity label — see PREP-277.
 */
export function DaySuggestionCard({
    customer,
    rank,
    date,
    onEventCreated,
    hideScheduleButton,
}: DaySuggestionCardProps): ReactNode {
    const navigate = useNavigate();
    const sendEvent = useAnalytics();
    const { t } = useTranslation('events');

    const { ref } = useInView({
        threshold: 0,
        onChange: (inView) => {
            if (inView) {
                sendEvent('recommended_customer_viewed', { customer_id: customer.customerId, rank });
            }
        },
        triggerOnce: true,
    });

    const handleCardClick = (): void => {
        sendEvent('event_recommendation_preview', { customer_id: customer.customerId });
        sendEvent('recommended_customer_clicked', { customer_id: customer.customerId, rank });
        void navigate(`/customers/${encodeURIComponent(customer.customerId)}`);
    };

    const handleScheduleClick = (e: React.MouseEvent): void => {
        e.stopPropagation();
        sendEvent('event_recommendation_clicked', { customer_id: customer.customerId });
        sendEvent('recommended_customer_event_scheduled', { customer_id: customer.customerId, rank });
        void DialogStateManager.show(EventCreateDialog, {
            customer,
            date,
            onCreated: onEventCreated,
            analyticsSource: 'recommendation',
        } satisfies EventCreateDialogProps);
    };

    const handleDismissClick = (e: React.MouseEvent): void => {
        e.stopPropagation();
        void DialogStateManager.show(SuggestionFeedbackDialog, { customer });
    };

    const addressText = [customer.address.zipCode, customer.address.city].filter(Boolean).join(' ');

    const tierLabelMap: Record<string, string> = {
        A: t('recommended_customers_tier_a'),
        B: t('recommended_customers_tier_b'),
        C: t('recommended_customers_tier_c'),
    };

    const tierLabel = customer.tier ? tierLabelMap[customer.tier] : undefined;

    return (
        <div ref={ref} onClick={handleCardClick} className="flex flex-col cursor-pointer py-2">
            <InfoTag icon={Store} size="xs">
                {customer.customerId}
            </InfoTag>
            <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col min-w-0">
                    <TruncatedText text={customer.customerName ?? ''} color="neutral-600" />
                    {(addressText || tierLabel) && (
                        <div className="flex flex-wrap items-center gap-x-cozy gap-y-0.5 min-w-0 text-neutral-500">
                            {addressText && (
                                <div className="flex items-center gap-tight min-w-0">
                                    <MapPinIcon className="size-3.5 shrink-0" />
                                    <TruncatedText text={addressText} size="xs" color="neutral-500" />
                                </div>
                            )}
                            {tierLabel && (
                                <div className="flex items-center gap-tight min-w-0">
                                    <NavigationIcon className="size-3 shrink-0" />
                                    <TruncatedText text={tierLabel} size="xs" color="neutral-500" />
                                </div>
                            )}
                        </div>
                    )}
                    <CustomerInfoTags customer={customer} maxTags={1} />
                </div>
                {!hideScheduleButton && (
                    <div className="flex items-center gap-1 shrink-0">
                        <IconButton
                            variant="secondary"
                            onClick={handleScheduleClick}
                            label={t('aria__schedule_event')}
                        >
                            <PlusIcon />
                        </IconButton>
                        <IconButton
                            variant="secondary"
                            onClick={handleDismissClick}
                            label={t('aria__dismiss_suggestion')}
                        >
                            <XIcon />
                        </IconButton>
                    </div>
                )}
            </div>
        </div>
    );
}
